/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Stacked / Compact message row — the SECOND chat display mode (Figma
// 2804:25812 master + the "Stacked / Compact" detail columns of
// 3412:5136 / 5207 / 4708 / 4897 / 5288 / 2869:5293). Slack/Discord
// style: a left avatar gutter, an inline header row (sender name +
// timestamp + read-checks), and the message body rendered FULL-WIDTH
// with NO bubble background. Consecutive same-sender messages collapse
// into one block — only the first row of a run shows the avatar +
// header; following rows reserve the gutter (and reveal a hover-time)
// and render body-only.
//
// The header + composer are unchanged across modes — only the message
// list rendering differs. `MessageRow` delegates here when
// `layoutMode === 'stacked'`; `ChatRoom` owns the grouping decision
// (showHeader) and resolves the per-message sender name/avatar.

import React, { useMemo, useState } from "react";
import { Image, Linking, Pressable, StyleSheet, Text, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { BadgeCheck, Check, CheckCheck, Clock, Info, Pin } from "lucide-react-native";
import { createTheme } from "../design-system/theme";
import { tokens } from "../design-system/tokens";
import type { Message } from "../sync/persistence";
import type { TextBody } from "../appEvent/types";
import { PreviewLabel } from "../appEvent/PreviewLabel";
import type { ChatMessageVariant } from "./ChatRoom.types";
import type { ReactionAggregate } from "./ReactionsRow";
import type { MessageMenuAction } from "./MessageActionMenu";
import { Avatar } from "../components/Avatar";
import { RichTextBody } from "./RichTextBody";
import { decodeBubbleMentions } from "./decodeBubbleBody";
import { extractEmbeddedLinkPreview } from "./parseMessageBody";
import { MrkdwnText } from "./MrkdwnText";
import { useTileMaxWidth } from "./tileConstraint";
import { tileMrkdwnPalette } from "./tileMrkdwnPalette";
import { ReactionsRow } from "./ReactionsRow";
import { MessageHoverHost } from "./MessageHoverHost";
import { VoiceMessageBubble } from "./VoiceMessageBubble";
import { ImageAttachmentCard } from "./attachments/ImageAttachmentCard";
import { DocAttachmentCard } from "./attachments/DocAttachmentCard";
import { VideoAttachmentCard } from "./attachments/VideoAttachmentCard";
import { LinkPreviewCard } from "./attachments/LinkPreviewCard";
import { LocationAttachmentCard } from "./attachments/LocationAttachmentCard";
import { GameTile } from "./games/GameTile";
import type { GameSession } from "../sync/useGameSessions";
import { PollView } from "../polls/PollView";
import { MeetingView } from "../meetings/MeetingView";
import { CallNotificationCard } from "./CallNotificationCard";
import { EditMessageBubble } from "./EditMessageBubble";
import { EventMessage } from "./EventMessage";
import { ThreadPreviewCard } from "./ThreadPreviewCard";
import { ReadByPopover } from "./ReadByPopover";

const READ_GREEN = "#3CCB4F";

export interface StackedMessageRowProps {
  readonly message: Message;
  readonly variant?: ChatMessageVariant | undefined;
  /** First message of a consecutive same-sender run — draws the
   *  avatar + name/time header. Collapsed lines pass `false`. */
  readonly showHeader: boolean;
  /** Resolved display name of this message's author (self or peer). */
  readonly senderName?: string | undefined;
  /** Group-chat verified badge next to the header name (Figma 7038:2148). */
  readonly senderVerified?: boolean | undefined;
  readonly senderAvatar?:
    | { readonly initials: string; readonly imageUri?: string }
    | undefined;
  readonly onReplyPress?: ((message: Message) => void) | undefined;
  readonly onMentionPress?: ((userId: string) => void) | undefined;
  /** Tapping the sender AVATAR opens the profile popover anchored to it. */
  readonly onAvatarPress?:
    | ((userId: string, anchorRef: React.RefObject<unknown>) => void)
    | undefined;
  readonly onRetryPress?: ((messageId: string) => void) | undefined;
  readonly reactions?: ReadonlyArray<ReactionAggregate> | undefined;
  readonly onToggleReaction?: ((emoji: string) => void) | undefined;
  readonly editedAt?: number | undefined;
  // "Forwarded" indicator — true for a forwarded copy (`com.ping.forward`,
  // unwrapped by the messages adapter). Renders a small italic header above the
  // body, mirroring the "(edited)" footer. Set on both sender + recipient copies.
  readonly forwarded?: boolean | undefined;
  readonly richBody?: TextBody | undefined;
  readonly isPinned?: boolean | undefined;
  readonly isSaved?: boolean | undefined;
  readonly isStarred?: boolean | undefined;
  readonly threadAffordance?:
    | {
        readonly count: number;
        readonly unreadCount?: number | undefined;
        readonly onPress: () => void;
        readonly participants?:
          | ReadonlyArray<{ readonly initials: string; readonly imageUri?: string | undefined }>
          | undefined;
        readonly lastReplierName?: string | undefined;
        readonly preview?: string | undefined;
      }
    | undefined;
  readonly onReactPress?: ((message: Message) => void) | undefined;
  readonly onReplyPressFromHover?: ((message: Message) => void) | undefined;
  /** Edit-from-hover (own text messages) — forwarded to the hover host's Edit
   *  chip, which gates on `isSelf`. */
  readonly onEditPressFromHover?: ((message: Message) => void) | undefined;
  readonly onMenuAction?:
    | ((action: MessageMenuAction, message: Message) => void)
    | undefined;
  /** Tap the reply-quote → jump/scroll to the referenced message. */
  readonly onReplyQuotePress?: ((messageId: string) => void) | undefined;
  /** Live game session for an interactive game tile (compact parity). */
  readonly gameSession?: GameSession | undefined;
  readonly onGameMove?:
    | ((sessionId: string, moveIdx: number, payload: string) => void)
    | undefined;
  readonly gameOpponentName?: string | undefined;
  /** Resolves a display name for a reader userId — enables the tick to open a "Read by" popover. */
  readonly resolveReaderName?: ((userId: string) => string | undefined) | undefined;
  /** Resolves an avatar URI for a reader userId — shown in the "Read by" popover. */
  readonly resolveReaderAvatar?: ((userId: string) => string | undefined) | undefined;
  /** Number of non-self group members; green double-check only when ALL have read. */
  readonly groupNonSelfMemberCount?: number | undefined;
}

export const StackedMessageRow = React.memo<StackedMessageRowProps>(
  function StackedMessageRow({
    message,
    variant,
    showHeader,
    senderName,
    senderVerified,
    senderAvatar,
    onReplyPress,
    onMentionPress,
    onAvatarPress,
    onRetryPress,
    reactions,
    onToggleReaction,
    editedAt,
    forwarded,
    richBody,
    isPinned,
    isSaved,
    isStarred,
    threadAffordance,
    onReactPress,
    onReplyPressFromHover,
    onEditPressFromHover,
    onMenuAction,
    onReplyQuotePress,
    gameSession,
    onGameMove,
    gameOpponentName,
    resolveReaderName,
    resolveReaderAvatar,
    groupNonSelfMemberCount,
  }) {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === "dark" ? "dark" : "light");
    // Thread side panel: cap the compact body (text + tiles) to the given width.
    const tileMax = useTileMaxWidth();
    // Compact/stacked rows are DE-BUBBLED (no brand fill), so BOTH sides use the
    // neutral/brand tile palette — never white ink (which would be invisible on
    // the app surface). `onBrandFill=false` for every compact row.
    const compactPalette = useMemo(() => tileMrkdwnPalette(false, theme), [theme]);
    const [hovered, setHovered] = useState(false);
    // Anchor for the avatar-tap profile popover.
    const senderAvatarRef = React.useRef<View | null>(null);
    const avatarInteractive =
      !message.isSelf && (onAvatarPress !== undefined || onMentionPress !== undefined);

    // Event/system rows render centered, outside the gutter flow.
    if (variant?.kind === "event") {
      return (
        <View accessibilityLabel="System event">
          <EventMessage
            kind={variant.type}
            variant={variant.variant}
            title={variant.title}
            detail={variant.detail ?? undefined}
            timestamp={new Date(message.timestamp)}
          />
        </View>
      );
    }

    // Call tiles are room-owned events — centred, no sender gutter.
    if (variant?.kind === "call") {
      return (
        <View className="flex flex-row justify-end py-1" accessibilityLabel="Call event">
          <CallNotificationCard
            status={variant.status}
            durationLabel={variant.durationLabel}
            hideTime
            {...(variant.callState !== undefined ? { state: variant.callState } : {})}
            {...(variant.startedAtMs !== undefined ? { startedAtMs: variant.startedAtMs } : {})}
            {...(variant.participants !== undefined ? { participants: variant.participants } : {})}
            {...(variant.extraCount !== undefined ? { extraCount: variant.extraCount } : {})}
            {...(variant.joined !== undefined ? { joined: variant.joined } : {})}
            {...(variant.onJoinLeave !== undefined ? { onJoinLeave: variant.onJoinLeave } : {})}
            {...(variant.notes !== undefined ? { notes: variant.notes } : {})}
            {...(variant.onOpenCallThread !== undefined ? { onOpenCallThread: variant.onOpenCallThread } : {})}
            {...(variant.callThreadHasMessages !== undefined ? { callThreadHasMessages: variant.callThreadHasMessages } : {})}
            {...(variant.actionable !== undefined ? { actionable: variant.actionable } : {})}
          />
        </View>
      );
    }

    const isFailed = message.deliveryState === "failed";
    const isPending = message.deliveryState === "pending";
    const formattedTime = useMemo(
      () => formatTime(message.timestamp),
      [message.timestamp],
    );

    const readByCount = message.readBy?.length ?? 0;
    const isRead = groupNonSelfMemberCount !== undefined
      ? readByCount >= groupNonSelfMemberCount && groupNonSelfMemberCount > 0
      : readByCount > 0;
    const isDelivered = message.deliveredAt !== undefined || isRead;

    const readReceiptRef = React.useRef<View | null>(null);
    const [readersOpen, setReadersOpen] = useState(false);

    const tileReaders = useMemo(
      () =>
        message.isSelf && resolveReaderName !== undefined
          ? (message.readBy ?? []).map((r) => {
              const avatarUri = resolveReaderAvatar?.(r.userId);
              return {
                userId: r.userId,
                name: resolveReaderName(r.userId) ?? 'Member',
                ...(avatarUri !== undefined ? { avatarUri } : {}),
                timestamp: r.timestamp,
              };
            })
          : [],
      [message.isSelf, message.readBy, resolveReaderName, resolveReaderAvatar],
    );
    const tickPressable = tileReaders.length > 0;

    // Bare tick icon — no Pressable wrapper. Used inside the combined
    // hover-meta hit-target so clicking time OR tick opens the popover.
    const tickIcon: React.ReactNode =
      message.isSelf && !isFailed
        ? isPending
          // PENDING ("sending…") affordance — was missing in compact (bubble
          // shows a "· Sending…" trailing state). A clock glyph keeps parity
          // without the extra text in the dense compact meta.
          ? <Clock size={12} strokeWidth={2} color={theme.colors.text.muted} />
          : isRead
            ? <CheckCheck size={12} strokeWidth={2} color={READ_GREEN} />
            : isDelivered
              ? <CheckCheck size={12} strokeWidth={2} color={theme.colors.text.muted} />
              : <Check size={12} strokeWidth={2} color={theme.colors.text.muted} />
        : null;


    // ── Body / tile content (rendered de-bubbled in the content column).
    let body: React.ReactNode;
    if (variant?.kind === "voice") {
      body = (
        <VoiceMessageBubble
          state={variant.state}
          durationMs={variant.durationMs}
          waveform={variant.waveform}
          progress={variant.progress}
          timestamp={new Date(message.timestamp)}
          isSelf={message.isSelf}
          stacked
          delivered={isDelivered}
          read={isRead}
          {...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
          onTogglePlay={variant.onTogglePlay}
          onRetry={variant.onRetry}
          {...(variant.transcript !== undefined ? { transcript: variant.transcript } : {})}
          {...(variant.onPlaybackError !== undefined
            ? { onPlaybackError: variant.onPlaybackError }
            : {})}
          {...(variant.resolveVoiceSource !== undefined
            ? { message, resolveVoiceSource: variant.resolveVoiceSource }
            : {})}
        />
      );
    } else if (variant?.kind === "image") {
      body = (
        <ImageAttachmentCard
          state={variant.state}
          aspectRatio={variant.aspectRatio}
          blurhash={variant.blurhash}
          imageUri={variant.imageUri}
          progress={variant.progress}
          caption={variant.caption}
          isSelf={message.isSelf}
          {...(variant.isSticker === true ? { isSticker: true } : {})}
          timeLabel={formatTime(message.timestamp)}
          delivered={isDelivered}
          read={isRead}
          {...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
          onTapOpen={variant.onTapOpen}
          onRetry={variant.onRetry}
        />
      );
    } else if (variant?.kind === "video") {
      body = (
        <VideoAttachmentCard
          state={variant.state}
          aspectRatio={variant.aspectRatio}
          blurhash={variant.blurhash}
          posterUri={variant.posterUri}
          videoUri={variant.videoUri}
          mimeType={variant.mimeType}
          durationLabel={variant.durationLabel}
          caption={variant.caption}
          progress={variant.progress}
          downloading={variant.downloading}
          downloadProgress={variant.downloadProgress}
          isSelf={message.isSelf}
          timeLabel={formatTime(message.timestamp)}
          delivered={isDelivered}
          read={isRead}
          {...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
          onTapOpen={variant.onTapOpen}
          onDownload={variant.onDownload}
          onFullscreen={variant.onFullscreen}
          onRetry={variant.onRetry}
        />
      );
    } else if (variant?.kind === "linkPreview") {
      body = (
        <LinkPreviewCard
          url={variant.url}
          title={variant.title}
          description={variant.description}
          imageUri={variant.imageUri}
          domain={variant.domain}
          isSelf={message.isSelf}
          onOpen={variant.onOpen}
        />
      );
    } else if (variant?.kind === "doc") {
      body = (
        <DocAttachmentCard
          state={variant.state}
          filename={variant.filename}
          mimeType={variant.mimeType}
          byteSize={variant.byteSize}
          pageCount={variant.pageCount}
          previewUri={variant.previewUri}
          progress={variant.progress}
          isSelf={message.isSelf}
          timeLabel={formatTime(message.timestamp)}
          delivered={isDelivered}
          read={isRead}
          {...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
          onTapPreview={variant.onTapPreview}
          onRetry={variant.onRetry}
        />
      );
    } else if (variant?.kind === "location") {
      body = (
        <LocationAttachmentCard
          lat={variant.lat}
          lng={variant.lng}
          {...(variant.label !== undefined ? { label: variant.label } : {})}
          isSelf={message.isSelf}
          {...(variant.onOpen !== undefined ? { onOpen: variant.onOpen } : {})}
          {...(variant.live !== undefined ? { live: variant.live } : {})}
          {...(variant.liveUntilTs !== undefined
            ? { liveUntilTs: variant.liveUntilTs }
            : {})}
          {...(variant.sharerAvatarUrl !== undefined
            ? { sharerAvatarUrl: variant.sharerAvatarUrl }
            : {})}
          timestamp={message.timestamp}
          delivered={isDelivered}
          read={isRead}
          {...(tileReaders.length > 0 ? { readers: tileReaders } : {})}
        />
      );
    } else if (variant?.kind === "game") {
      // Interactive tile (live session + moves) — parity with the bubble
      // renderer, not the frozen create-phase GameMessageCard.
      body = (
        <GameTile
          gameKind={variant.gameKind}
          sessionId={variant.sessionId}
          isCreator={message.isSelf}
          compact
          session={gameSession}
          {...(onGameMove !== undefined ? { onMove: onGameMove } : {})}
          selfName="You"
          {...(gameOpponentName !== undefined ? { opponentName: gameOpponentName } : {})}
        />
      );
    } else if (variant?.kind === "poll") {
      body = (
        <PollView
          poll={variant.poll}
          counts={variant.counts}
          {...(variant.myVote !== undefined ? { myVote: variant.myVote } : {})}
          {...(variant.myVotes !== undefined ? { myVotes: variant.myVotes } : {})}
          total={variant.total}
          onVote={variant.onVote}
          {...(variant.votersByOption !== undefined
            ? { votersByOption: variant.votersByOption }
            : {})}
          {...(variant.onViewVotes !== undefined ? { onViewVotes: variant.onViewVotes } : {})}
          isSelf={message.isSelf}
          delivered={message.deliveredAt !== undefined || (message.readBy?.length ?? 0) > 0}
        />
      );
    } else if (variant?.kind === "meeting") {
      body = (
        <MeetingView
          content={variant.content}
          counts={variant.counts}
          {...(variant.myRsvp !== undefined ? { myRsvp: variant.myRsvp } : {})}
          {...(variant.onRsvp !== undefined ? { onRsvp: variant.onRsvp } : {})}
          {...(variant.onOpenLink !== undefined ? { onOpenLink: variant.onOpenLink } : {})}
          {...(variant.onCopyLink !== undefined ? { onCopyLink: variant.onCopyLink } : {})}
          {...(variant.onEdit !== undefined ? { onEdit: variant.onEdit } : {})}
          {...(variant.onCancelMeeting !== undefined
            ? { onCancelMeeting: variant.onCancelMeeting }
            : {})}
          {...(variant.onShowResponses !== undefined
            ? { onShowResponses: variant.onShowResponses }
            : {})}
          {...(variant.onJoinCall !== undefined ? { onJoinCall: variant.onJoinCall } : {})}
          isSelf={message.isSelf}
          delivered={message.deliveredAt !== undefined || (message.readBy?.length ?? 0) > 0}
          timeLabel={formatTime(message.timestamp)}
        />
      );
    } else if (variant?.kind === "edit") {
      body = (
        <EditMessageBubble
          variant={variant.variant}
          isSelf={message.isSelf}
          // Raw envelope body so `MrkdwnText` decodes it + renders mentions /
          // bold / links. `compact` de-bubbles the tile onto the neutral
          // surface, matching every other stacked row (no brand-blue fill).
          body={message.body}
          editedAt={new Date(variant.editedAt)}
          compact
          {...(message.mentions !== undefined ? { mentions: message.mentions } : {})}
          {...(onMentionPress !== undefined ? { onMentionPress } : {})}
        />
      );
    } else if (richBody !== undefined) {
      body = (
        <RichTextBody
          body={richBody}
          textStyle={{ ...styles.bodyText, color: theme.colors.text.primary }}
          // Shared tile palette (de-bubbled → `onBrandFill=false`) so mention
          // pills match bubble mode + the mrkdwn path below. Previously used
          // `accent.indigo` here vs `accent.brand` elsewhere — a hue drift.
          mentionColor={compactPalette.mention}
          mentionBackgroundColor={compactPalette.mentionBackground}
          {...(onMentionPress !== undefined ? { onMentionPress } : {})}
        />
      );
    } else {
      // Decode the envelope + render full Slack-mrkdwn (bold / italic /
      // strike / code / links / @mentions + quotes / code blocks / lists),
      // matching bubble mode. `MrkdwnText` decodes the `{kind,payload}` JSON
      // so it never renders raw in compact.
      const linkPreview = extractEmbeddedLinkPreview(message.body);
      const textNode = (
        <MrkdwnText
          body={message.body}
          {...(message.mentions !== undefined ? { mentions: message.mentions } : {})}
          {...(() => {
            const km = decodeBubbleMentions(message.body);
            return km.length > 0 ? { knownMentions: km } : {};
          })()}
          textStyle={{ ...styles.bodyText, color: theme.colors.text.primary }}
          palette={compactPalette}
          {...(onMentionPress !== undefined ? { onMentionPress } : {})}
          onLinkPress={(url) => {
            void Linking.openURL(url).catch(() => undefined);
          }}
        />
      );
      body =
        linkPreview !== undefined ? (
          <View className="gap-1 items-start">
            {textNode}
            <LinkPreviewCard
              url={linkPreview.url}
              title={linkPreview.title}
              description={linkPreview.description}
              imageUri={linkPreview.imageUri}
              onOpen={() => {
                void Linking.openURL(linkPreview.url).catch(() => undefined);
              }}
            />
          </View>
        ) : (
          textNode
        );
    }

    // These tile kinds embed their own time+tick (in a footer row or pill
    // overlay), so the hoverMetaRow would be a duplicate.
    const tileHasMeta =
      variant?.kind === 'voice' ||
      variant?.kind === 'image' ||
      variant?.kind === 'video' ||
      variant?.kind === 'doc' ||
      variant?.kind === 'location';

    const content = (
      <View style={styles.content}>
        {showHeader ? (
          <View style={styles.headerRow}>
            <Text
              style={[styles.senderName, { color: theme.colors.text.secondary }]}
              numberOfLines={1}
            >
              {senderName ?? (message.isSelf ? "You" : "")}
            </Text>
            {/* Verified badge is a PEER signal — never shown on your own
                message (matches bubble mode, which renders it only in the
                other-sender node). */}
            {senderVerified === true && !message.isSelf ? (
              <BadgeCheck
                size={13}
                strokeWidth={2}
                color={theme.colors.accent.verified}
              />
            ) : null}
            {tickPressable ? (
              <Pressable
                ref={readReceiptRef}
                onPress={() => setReadersOpen(true)}
                style={styles.headerMeta}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="See who read this message"
              >
                <Text style={[styles.time, { color: theme.colors.text.muted }]}>
                  {formattedTime}
                </Text>
                {tickIcon}
              </Pressable>
            ) : (
              <>
                <Text style={[styles.time, { color: theme.colors.text.muted }]}>
                  {formattedTime}
                </Text>
                {tickIcon}
              </>
            )}
          </View>
        ) : null}

        {message.inReplyTo ? (
          // Figma 7038:2000 stacked Reply inset — #FFFFFF@10% fill with a
          // 1px #000000@20% stroke, radius 8. Quoted sender name is #FFFFFF.
          // Tapping jumps to the referenced message.
          <Pressable
            onPress={
              onReplyQuotePress !== undefined &&
              message.inReplyTo.messageId !== undefined
                ? () => onReplyQuotePress(String(message.inReplyTo?.messageId))
                : undefined
            }
            accessibilityRole={onReplyQuotePress !== undefined ? "button" : undefined}
            accessibilityLabel="Go to replied message"
            style={[
              styles.replyCard,
              {
                // Compact is de-bubbled → THEMED inset + text (the old
                // white-on-white/10 name was invisible in light mode).
                backgroundColor: theme.colors.surface.secondary,
                borderWidth: 1,
                borderColor: theme.colors.border.subtle,
              },
            ]}
          >
            <View style={styles.replyRow}>
              {message.inReplyTo.thumbnailUri !== undefined &&
              message.inReplyTo.thumbnailUri.length > 0 ? (
                <Image
                  source={{ uri: message.inReplyTo.thumbnailUri }}
                  style={styles.replyThumb}
                  resizeMode="cover"
                  accessibilityLabel={
                    message.inReplyTo.thumbnailKind === 'video'
                      ? 'Video reply thumbnail'
                      : 'Photo reply thumbnail'
                  }
                />
              ) : null}
              <View style={styles.replyTextCol}>
                {message.inReplyTo.senderName !== undefined &&
                message.inReplyTo.senderName.length > 0 ? (
                  <Text
                    style={[styles.replyName, { color: theme.colors.text.primary }]}
                    numberOfLines={1}
                  >
                    {message.inReplyTo.senderName}
                  </Text>
                ) : null}
                <PreviewLabel
                  text={message.inReplyTo.preview}
                  color={theme.colors.text.secondary}
                  style={styles.replyPreview}
                  numberOfLines={2}
                />
              </View>
            </View>
          </Pressable>
        ) : null}

        {/* Body + collapsed-row hover meta side-by-side so the time+tick
            are vertically centered with the message body, not below it. */}
        <View style={styles.bodyRow}>
          <View style={[styles.bodyFill, tileMax !== null ? { maxWidth: tileMax } : null]}>{body}</View>
          {!showHeader && hovered && !tileHasMeta ? (
            tickPressable ? (
              <Pressable
                ref={readReceiptRef}
                onPress={() => setReadersOpen(true)}
                style={styles.hoverMetaRow}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="See who read this message"
              >
                <Text style={[styles.time, { color: theme.colors.text.muted }]}>
                  {formattedTime}
                </Text>
                {tickIcon}
              </Pressable>
            ) : (
              <View style={styles.hoverMetaRow}>
                <Text style={[styles.time, { color: theme.colors.text.muted }]}>
                  {formattedTime}
                </Text>
                {tickIcon}
              </View>
            )
          ) : null}
        </View>

        {forwarded === true ? (
          // "(forwarded)" line — under the body, next to "(edited)" and styled
          // identically (themed secondary text). Set on both the forwarder's
          // echo and every recipient's copy.
          <Text style={[styles.forwarded, { color: theme.colors.text.secondary }]}>
            (forwarded)
          </Text>
        ) : null}

        {editedAt !== undefined ? (
          <Text style={[styles.edited, { color: theme.colors.text.secondary }]}>
            (edited)
          </Text>
        ) : null}

        {isFailed ? (
          // Undelivered send — explicit "Not delivered · Resend" affordance
          // (#FF5353). The whole row is tappable; "Resend" is emphasised as the
          // actionable link so it reads as clickable, not just a status caption.
          <Pressable
            style={styles.errorRow}
            {...(onRetryPress !== undefined
              ? {
                  onPress: () => onRetryPress(message.id),
                  accessibilityRole: "button" as const,
                  accessibilityLabel: "Not delivered. Tap to resend.",
                }
              : {})}
          >
            <Info size={10} strokeWidth={2} color="#FF5353" />
            <Text style={[styles.errorText, { color: "#FF5353" }]}>
              Not delivered
            </Text>
            <Text style={[styles.errorText, { color: "#FF5353" }]}>·</Text>
            <Text
              style={[
                styles.errorText,
                { color: "#FF5353", fontWeight: "700", textDecorationLine: "underline" },
              ]}
            >
              Resend
            </Text>
          </Pressable>
        ) : null}

        {isPinned === true ? (
          <View style={styles.footerRow}>
            <Pin size={12} strokeWidth={2} color={theme.colors.text.secondary} />
            <Text style={[styles.footerLabel, { color: theme.colors.text.secondary }]}>
              Message pinned
            </Text>
          </View>
        ) : null}

        {threadAffordance !== undefined ? (
          <View style={styles.footerRow}>
            <ThreadPreviewCard
              count={threadAffordance.count}
              onPress={threadAffordance.onPress}
              {...(threadAffordance.unreadCount !== undefined
                ? { unreadCount: threadAffordance.unreadCount }
                : {})}
              {...(threadAffordance.participants !== undefined
                ? { participants: threadAffordance.participants }
                : {})}
              {...(threadAffordance.lastReplierName !== undefined
                ? { lastReplierName: threadAffordance.lastReplierName }
                : {})}
              {...(threadAffordance.preview !== undefined
                ? { preview: threadAffordance.preview }
                : {})}
            />
          </View>
        ) : null}

        {reactions !== undefined && reactions.length > 0 ? (
          <ReactionsRow
            reactions={reactions}
            isSelf={false}
            {...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
          />
        ) : null}
        {/* Readers popover — anchored to the header tick or hover-time tick; opens on tap. */}
        <ReadByPopover
          anchorRef={readReceiptRef as { current: unknown }}
          open={readersOpen}
          onClose={() => setReadersOpen(false)}
          placement="top"
          align={message.isSelf ? 'end' : 'start'}
          readers={tileReaders}
        />
      </View>
    );

    // Discord/Slack-style hover tint on the whole row (web/desktop) —
    // Figma 2804:25812 stacked hover. Subtle so it reads as a focus cue.
    const hoverTint = scheme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";
    const row = (
      <View
        style={[
          styles.row,
          showHeader ? styles.rowWithHeader : styles.rowCollapsed,
          hovered ? { backgroundColor: hoverTint } : null,
        ]}
        // Row hover tint on both web (react-native-web) and desktop
        // (react-native-macos) — both deliver View mouse events. The cast
        // covers the RN `ViewProps` type not officially declaring them.
        {...({
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        } as object)}
      >
        <View style={styles.gutter}>
          {showHeader && senderAvatar !== undefined ? (
            <Pressable
              ref={senderAvatarRef}
              onPress={
                !message.isSelf && onAvatarPress !== undefined
                  ? () => onAvatarPress(message.senderId, senderAvatarRef)
                  : !message.isSelf && onMentionPress !== undefined
                    ? () => onMentionPress(message.senderId)
                    : undefined
              }
              accessibilityRole={avatarInteractive ? 'button' : undefined}
              accessibilityLabel={avatarInteractive ? 'Open profile' : undefined}
            >
              <Avatar
                initials={senderAvatar.initials}
                {...(senderAvatar.imageUri !== undefined
                  ? { imageUri: senderAvatar.imageUri }
                  : {})}
                size="small"
                shape="rounded"
              />
            </Pressable>
          ) : null}
        </View>
        {content}
      </View>
    );

    // Hover action strip (web/desktop) — same panel as bubble mode.
    return (
      <MessageHoverHost
        message={message}
        isSelf={message.isSelf}
        stacked
        {...(onReactPress !== undefined ? { onReact: onReactPress } : {})}
        {...(onReplyPressFromHover !== undefined
          ? { onReply: onReplyPressFromHover }
          : onReplyPress !== undefined
          ? { onReply: onReplyPress }
          : {})}
        {...(onEditPressFromHover !== undefined
          ? { onEdit: onEditPressFromHover }
          : {})}
        {...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
        {...(onMenuAction !== undefined ? { onMenuAction } : {})}
        {...(isPinned !== undefined ? { isPinned } : {})}
        {...(isSaved !== undefined ? { isSaved } : {})}
        {...(isStarred !== undefined ? { isStarred } : {})}
      >
        {row}
      </MessageHoverHost>
    );
  },
);

StackedMessageRow.displayName = "StackedMessageRow";

function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(timestamp));
}

const styles = StyleSheet.create({
  // "Conversation Block" — Figma 3412:5136 stacked column. Row gap 8,
  // paddingHorizontal 12. First-of-run gets paddingTop 6 to separate
  // sender blocks; collapsed lines tighten to ~2px between body lines.
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingHorizontal: 12,
  },
  rowWithHeader: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  rowCollapsed: {
    paddingTop: 1,
    paddingBottom: 1,
  },
  // 36px avatar gutter (Avatar is 32; the 36 box reserves space so
  // collapsed body text stays at the same indent).
  gutter: {
    width: 36,
    flexShrink: 0,
    alignItems: "center",
    paddingTop: 2,
  },
  gutterTime: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  content: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  senderName: {
    fontSize: 14,
    lineHeight: 19.6,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
    flexShrink: 1,
  },
  time: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  bodyText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 16,
    lineHeight: 22.4,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  // Stacked reply quote — radius 8, white/10 surface (Figma 3412:5136
  // stacked Reply instance), inner text paddingHorizontal 8.
  replyCard: {
    borderRadius: 8,
    padding: 4,
    paddingHorizontal: 8,
    gap: 2,
    alignSelf: "flex-start",
  },
  replyRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  replyThumb: {
    width: 32,
    height: 32,
    borderRadius: 6,
  },
  replyTextCol: {
    flex: 1,
    gap: 2,
  },
  replyName: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: `${tokens.typography.fontWeight.bold}` as never,
  },
  replyPreview: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  edited: {
    fontSize: 12,
    lineHeight: 17,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  forwarded: {
    fontSize: 12,
    lineHeight: 17,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  errorRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  errorText: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  resend: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: `${tokens.typography.fontWeight.bold}` as never,
    textDecorationLine: "underline",
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  footerLabel: {
    fontSize: 12,
    lineHeight: 17,
    fontWeight: `${tokens.typography.fontWeight.regular}` as never,
  },
  threadLink: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: `${tokens.typography.fontWeight.bold}` as never,
  },
  bodyRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  bodyFill: {
    flex: 1,
    minWidth: 0,
  },
  hoverMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  headerMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});
